<template>
    <Card :title="$t('labels.integration')">
        <div class="card-body">
            <div class="row">
                <FormGroup class="col-lg-6" :label="$t('labels.name')" required>
                    <Input
                        v-model="integrationName"
                    />
                </FormGroup>
                <FormGroup class="col-lg-6" :label="$t('labels.type')" required>
                    <Select
                        :model-value="selectedType?.key"
                        @update:model-value="selectType"
                        :options="typeOptions"
                        :selected-value="selectedType?.value"
                        option-key="key"
                        option-value="value"
                    />
                </FormGroup>
                <template v-if="isMarketplace">
                    <FormGroup class="col-lg-6" :label="$t('labels.service')" required>
                        <Select
                            :model-value="selectedIntegrationService?.alias"
                            @update:model-value="selectIntegrationService"
                            :options="marketplaceServices"
                            :selected-value="selectedIntegrationService?.name"
                            option-key="alias"
                        />
                    </FormGroup>
                    <FormGroup class="col-lg-6" :label="$t('labels.api_credentials')">
                        <AppAutoComplete
                            @update:model-value="credential = $event"
                            :url="`/projects/${$route.params.projectUuid}/core/api-credentials`"
                            :model-value="credential?.uuid"
                            :selected-value="credential?.name"
                            :query-params="{
                                service: selectedIntegrationService?.alias
                            }"
                        >
                            <template #optionContent>
                                <span v-if="!credential" class="text-muted">{{ $t('labels.empty') }}</span>
                                <div v-else>
                                    <span class="mr-2">{{ credential.name }}</span>
                                    <span v-if="credential.is_active" class="badge badge-success">Активні</span>
                                    <span v-else class="badge badge-danger">Неактивні</span>
                                </div>
                            </template>
                            <template #optionItem="{option}">
                                <span class="mr-2">{{ option.name }}</span>
                                <span v-if="option.is_active" class="badge badge-success">Активні</span>
                                <span v-else class="badge badge-danger">Неактивні</span>
                            </template>
                        </AppAutoComplete>
                    </FormGroup>
                </template>
                <template v-if="isSupplier">
                    <FormGroup class="col-lg-2" :label="$t('labels.service')" required>
                        <Select
                            :model-value="dropshipperService?.key"
                            @update:model-value="dropshipperService = $event"
                            :options="dropshipperServices"
                            :selected-value="dropshipperService?.value"
                            option-key="key"
                            option-value="value"
                        />
                    </FormGroup>
                    <FormGroup class="col-lg-6" :label="$t('labels.file_url')" required>
                        <Input
                            v-model="fileUrl"
                        />
                    </FormGroup>
                    <FormGroup class="col-lg-2" :label="$t('labels.unique_product_field')" required>
                        <Select
                            :model-value="uniqueProductField?.key"
                            @update:model-value="uniqueProductField = $event"
                            :options="uniqueProductFieldsOptions"
                            :selected-value="uniqueProductField?.value"
                            option-key="key"
                            option-value="value"
                        />
                    </FormGroup>
                    <FormGroup class="col-lg-2" :label="$t('labels.unique_sku_enabled')">
                        <AppCheckbox v-model="uniqueSkuEnabled"/>
                    </FormGroup>
                </template>
            </div>
            <Alert
                v-if="requiredLanguageAliases.length"
                type="info"
            >
                <span class="font-weight-semibold mr-2">{{ $t(`alerts.info`) }}</span>
                {{ ` ${$t('alerts.integration_service_info', {
                    languageName: requiredLanguageAliases.join(', ')
                })}` }}
                <a href="#" class="btn alpha-blue text-blue-800 border-blue-600 mt-2" @click="addMissingLanguages">{{ $t('alerts.сonnect_automatically') }}</a>
            </Alert>
        </div>
        <div class="card-footer d-flex justify-content-end">
            <Button color="indigo" @click="create" :disabled="isCreateDisabled">
                <i class="icon-floppy-disk mr-2"></i>
                {{ $t('buttons.create') }}
            </Button>
        </div>
    </Card>
</template>
<script>
import Card from '../../../components/new/CardComponent.vue'
import FormGroup from '../../../components/new/form/group/FormGroup.vue'
import Select from '../../../components/new/form/select/AppSelect.vue'
import Input from '../../../components/new/form/Input.vue'
import Button from '../../../components/new/button/AppButton.vue'
import Alert from '../../../components/AlertComponent.vue'
import AppAutoComplete from '../../../components/new/form/autocomplete/AppAutoComplete.vue'
import AppCheckbox from '../../../components/new/form/checkbox/AppCheckbox.vue'
import { httpClient } from '../../../services/HttpClient'
import { mapGetters } from 'vuex'
import { IntegrationServiceEnum } from '../../enums/IntegrationServiceEnum'
import { UniqueProductFieldEnum } from '../../enums/UniqueProductFieldEnum'
import { DropshipperServiceEnum } from '../../enums/DropshipperServiceEnum'
import MessageNotificator from "../../../services/MessageNotificator";

const IntegrationTypeEnum = {
    MARKETPLACE: 'marketplace',
    SUPPLIER: 'supplier'
}

export default {
    name: 'IntegrationCreatePage',
    components: {
        Card, FormGroup, Select, Input, Button, Alert, AppAutoComplete, AppCheckbox
    },
    data() {
        return {
            integrationName: '',
            projectLanguages: [],
            selectedType: null,
            typeOptions: [
                {key: IntegrationTypeEnum.MARKETPLACE, value: this.$t('labels.type_marketplace')},
                {key: IntegrationTypeEnum.SUPPLIER, value: this.$t('labels.type_supplier')}
            ],
            selectedIntegrationService: null,
            credential: null,
            fileUrl: '',
            dropshipperService: {
                key: DropshipperServiceEnum.PROM,
                value: this.$t(`integrationServices.${DropshipperServiceEnum.PROM}`)
            },
            uniqueProductField: {key: UniqueProductFieldEnum.SKU, value: this.$t('labels.sku')},
            uniqueSkuEnabled: false,
            uniqueProductFieldsOptions: [
                {key: UniqueProductFieldEnum.SKU, value: this.$t('labels.sku')},
                {key: UniqueProductFieldEnum.SOURCE_ID, value: this.$t('labels.external_id')}
            ]
        }
    },
    methods: {
        create() {
            const request = {
                name: this.integrationName,
                service: this.isSupplier ? IntegrationServiceEnum.SUPPLIER : this.selectedIntegrationService.alias
            }

            if (this.isMarketplace) {
                request.api_credentials_uuid = this.credential?.uuid
            }

            if (this.isSupplier) {
                request.dropshipper_service = this.dropshipperService.key
                request.dropshipper_external_url = this.fileUrl
                request.dropshipper_unique_product_field = this.uniqueProductField.key
                request.dropshipper_unique_sku_enabled = this.uniqueSkuEnabled
            }

            httpClient.post(`/projects/${this.$route.params.projectUuid}/pim/channels`, request).then((response) => {
                this.$router.push({ name: 'MainInformation', params: { projectUuid: this.project.uuid, integrationUuid: response.data.data.uuid }})
            })
        },
        async fetchProjectLanguages() {
            await httpClient.get('/list/languages').then((response) => {
                this.projectLanguages = response.data.data
            })
        },
        selectType(selectedType) {
            this.selectedType = selectedType
            this.selectedIntegrationService = null
            this.credential = null
        },
        async selectIntegrationService(selectedIntegrationService) {
            if (!this.projectLanguages.length) {
                await this.fetchProjectLanguages()
            }

            this.selectedIntegrationService = selectedIntegrationService
        },
        addMissingLanguages() {
            httpClient.get('/customization/languages/autocomplete', {
                aliases: this.requiredLanguageAliases
            }).then(async (response) => {
                await Promise.all(response.data.data.map((finedLanguage) => {
                    return httpClient.post(`/customization/languages/${finedLanguage.uuid}/attach`).then((response) => {
                        new MessageNotificator().success(this.$t('alerts.languages_connected_successfully'))
                    })
                }))

                this.fetchProjectLanguages()
            })
        }
    },
    computed: {
        isMarketplace() {
            return this.selectedType?.key === IntegrationTypeEnum.MARKETPLACE
        },
        isSupplier() {
            return this.selectedType?.key === IntegrationTypeEnum.SUPPLIER
        },
        isCreateDisabled() {
            if (this.integrationName === '' || this.selectedType === null) {
                return true
            }

            if (this.isMarketplace) {
                return !this.selectedIntegrationService || this.requiredLanguageAliases.length > 0
            }

            return !this.fileUrl
        },
        dropshipperServices() {
            const dropshipperServicesOptions = Object.values(DropshipperServiceEnum).map((dropshipperService) => {
                return {key: dropshipperService, value: this.$t(`integrationServices.${dropshipperService}`)}
            })

            return dropshipperServicesOptions
        },
        marketplaceServices() {
            let integrationServices = Object.values(IntegrationServiceEnum)
                .filter((integrationService) => integrationService !== IntegrationServiceEnum.SUPPLIER);
            let integrationServicesOptions = [];

            for (let integrationService of integrationServices) {
                const isShopify = integrationService === 'shopify';

                integrationServicesOptions.push({
                    name: this.$t(`integrationServices.${integrationService}`),
                    alias: integrationService,
                    language_aliases: isShopify ? ['uk'] : ['uk', 'ru'],
                })
            }

            return integrationServicesOptions;
        },
        requiredLanguageAliases() {
            if (this.selectedIntegrationService === null) {
                return [];
            }

            if (this.projectLanguageAliases.length === 0) {
                return this.selectedIntegrationService.language_aliases
            }

            let requiredLanguagesAliases = this.selectedIntegrationService.language_aliases
                .filter(item => !this.projectLanguageAliases.includes(item));

            return requiredLanguagesAliases.length > 0 ? requiredLanguagesAliases : [];
        },
        projectLanguageAliases() {
            return this.projectLanguages.map((projectLanguage) => projectLanguage.alias);
        },
        ...mapGetters('route', ['project'])
    }
}
</script>
